import React, { useState, useRef } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/Drones.css"; // adjust path if needed
import { exportToCSV } from "../utils/exportUtils";

const GET_BUYER_PILOTS = gql`
  query BuyerPilots {
    buyerPilots {
      buyerPilotId
      pilotName
      pilotCompany
      location
      availability
      specification
      price {
        perHour
        perDay
      }
      profilePhoto {
        url
        filename
        uploadedAt
      }
      certifications {
        url
        filename
        uploadedAt
      }
      description
      newemail
      newphoneNumber
      adminStatus
      buyerStatus
      buyerId
      buyer {
        id
        buyerId
        firebaseUid
        name
        email
        phoneNumber
        token
        password
        createdAt
        updatedAt
        welcomeNotificationSent
        phone
        addresses {
          street
          city
          state
          postalCode
          country
          countryCode
        }
      }
      createdAt
      updatedAt
      _source
      displayId
      contactEmail
      contactPhone
      bankDetails {
        accountHolderName
        bankName
        accountNumber
        ifscCode
      }
      bankValididation {
        razorpayContactId
        razorpayFundAccountId
        razorpayStatus
        razorpayValidationId
        razorpayVerificationPaymentId
        razorpayVerified
        razorpayVerifiedAt
      }
    }
  }
`;

const UPDATE_PILOT_STATUS = gql`
  mutation AdminUpdateBuyerPilotStatus(
    $buyerPilotId: String!,
    $adminStatus: String!
  ) {
    adminUpdateBuyerPilotStatus(
      buyerPilotId: $buyerPilotId,
      adminStatus: $adminStatus
    ) {
      buyerPilotId
      adminStatus
      buyerStatus
      pilotName
    }
  }
`;

const DELETE_PILOT = gql`
  mutation DeleteBuyerPilot($buyerPilotId: String!) {
    deleteBuyerPilot(buyerPilotId: $buyerPilotId)
  }
`;

function BuyerPilots() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedBuyer, setExpandedBuyer] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "buyerId", direction: "asc" });
  const [viewingPilot, setViewingPilot] = useState(null);
  const [viewingContent, setViewingContent] = useState({ type: null, content: null, title: null });
  const [viewingBankDetails, setViewingBankDetails] = useState(null); // for bank details modal
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const tableRef = useRef(null);

  const ITEMS_PER_PAGE = 10;
  const PRODUCTS_PER_PAGE = 10;

  const { loading, error: queryError, data, refetch } = useQuery(GET_BUYER_PILOTS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [updateStatusMutation] = useMutation(UPDATE_PILOT_STATUS);
  const [deletePilotMutation] = useMutation(DELETE_PILOT);

  const pilots = data?.buyerPilots || [];
  const error = queryError ? queryError.message : null;

  const updateStatus = async (buyerPilotId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${buyerPilotId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: {
          buyerPilotId,
          adminStatus: newStatus.toLowerCase()
        },
      });
      refetch();
    } catch (err) {
      alert(`❌ Failed to update status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${buyerPilotId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  const deletePilot = async (buyerPilotId) => {
    if (!window.confirm("Delete this pilot registration?")) return;

    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${buyerPilotId}-deleting`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await deletePilotMutation({
        variables: { buyerPilotId }
      });
      refetch();
      if (viewingPilot && viewingPilot.buyerPilotId === buyerPilotId) {
        setViewingPilot(null);
      }
      if (viewingBankDetails && viewingBankDetails.buyerPilotId === buyerPilotId) {
        setViewingBankDetails(null);
      }
    } catch (err) {
      alert(`❌ Failed to delete pilot: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${buyerPilotId}-deleting`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  const viewContent = (type, content, title) => {
    setViewingContent({ type, content, title });
  };

  const closeContent = () => {
    setViewingContent({ type: null, content: null, title: null });
  };

  const viewBankDetails = (pilot) => {
    setViewingBankDetails(pilot);
  };

  const closeBankDetails = () => {
    setViewingBankDetails(null);
  };

  const groupedByBuyer = pilots.reduce((acc, pilot) => {
    const buyerId = pilot.buyerId;
    if (!acc[buyerId]) {
      acc[buyerId] = {
        buyerId: buyerId,
        buyerInfo: pilot.buyer,
        pilots: []
      };
    }
    acc[buyerId].pilots.push(pilot);
    return acc;
  }, {});

  const buyerGroups = Object.values(groupedByBuyer).sort((a, b) =>
    a.buyerId.localeCompare(b.buyerId)
  );

  const buyerGroupsWithStats = buyerGroups.map(buyer => {
    const totalPilots = buyer.pilots.length;
    const approvedPilots = buyer.pilots.filter(p => p.adminStatus === 'approved').length;
    const pendingPilots = buyer.pilots.filter(p => !p.adminStatus || p.adminStatus === 'pending').length;
    const rejectedPilots = buyer.pilots.filter(p => p.adminStatus === 'rejected').length;
    const deactivatedPilots = buyer.pilots.filter(p => p.adminStatus && p.adminStatus.toLowerCase().includes('deactivated')).length;

    return {
      ...buyer,
      totalPilots,
      approvedPilots,
      pendingPilots,
      rejectedPilots,
      deactivatedPilots
    };
  });

  const filteredBuyers = buyerGroupsWithStats.filter(buyer => {
    const matchesSearch =
      searchTerm === "" ||
      buyer.buyerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      buyer.buyerInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      buyer.buyerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      buyer.pilots.some(pilot =>
        pilot.pilotName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilot.pilotCompany?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilot.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (selectedStatus === "all") return matchesSearch;

    const filteredPilots = buyer.pilots.filter(pilot => {
      const status = pilot.adminStatus?.toLowerCase() || "pending";
      if (selectedStatus === "deactivated") {
        return status.includes("deactivated");
      }
      return status === selectedStatus;
    });

    return matchesSearch && filteredPilots.length > 0;
  });

  const sortedBuyers = [...filteredBuyers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'buyerId' || key === 'name' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const totalFilteredPages = Math.ceil(sortedBuyers.length / ITEMS_PER_PAGE);
  const paginatedBuyers = sortedBuyers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getFilteredPilotsForBuyer = (buyerId) => {
    const buyer = groupedByBuyer[buyerId];
    if (!buyer) return [];

    return buyer.pilots.filter(pilot => {
      const status = pilot.adminStatus?.toLowerCase() || "pending";
      if (selectedStatus === "all") return true;
      if (selectedStatus === "deactivated") {
        return status.includes("deactivated");
      }
      return status === selectedStatus;
    });
  };

  const getPaginatedPilotsForBuyer = (buyerId) => {
    const filteredPilots = getFilteredPilotsForBuyer(buyerId);
    const currentProductsPage = productsPage[buyerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;

    return {
      pilots: filteredPilots.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredPilots.length / PRODUCTS_PER_PAGE),
      totalPilots: filteredPilots.length
    };
  };

  const handleProductsPageChange = (buyerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [buyerId]: page
    }));
  };

  const viewPilotDetails = (pilot) => {
    setViewingPilot(pilot);
  };

  const closePilotDetails = () => {
    setViewingPilot(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  const statusStats = {
    all: pilots.length,
    approved: pilots.filter(p => p.adminStatus === "approved").length,
    pending: pilots.filter(p => !p.adminStatus || p.adminStatus === "pending").length,
    rejected: pilots.filter(p => p.adminStatus === "rejected").length,
    deactivated: pilots.filter(p => p.adminStatus && p.adminStatus.toLowerCase().includes("deactivated")).length,
  };

  const totalPilots = pilots.length;
  const availablePilots = pilots.filter(p => p.availability).length;

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const formatPrice = (price) => {
    if (!price) return "—";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleExport = () => {
    const headers = ["Pilot ID", "Buyer ID", "Name", "Email", "Phone", "Location", "Availability", "Rate/hr", "Status", "Buyer Name", "Bank Verified"];
    const rows = pilots.map(p => [
      p.buyerPilotId,
      p.buyerId,
      p.pilotName,
      p.newemail,
      p.newphoneNumber,
      p.location,
      p.availability ? "Available" : "Busy",
      p.price?.perHour ? `₹${p.price.perHour}` : "—",
      p.adminStatus || "pending",
      p.buyer?.name || "",
      p.bankValididation?.razorpayVerified ? "Yes" : "No"
    ]);
    exportToCSV(headers, rows, `buyer_pilots_export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const renderContentButtons = (pilot) => {
    const hasSpecification = pilot.specification && pilot.specification.trim().length > 0;
    const hasDescription = pilot.description && pilot.description.trim().length > 0;
    const hasCertificates = pilot.certifications && pilot.certifications.length > 0;
    const hasProfilePhoto = pilot.profilePhoto && pilot.profilePhoto.url;

    return (
      <div className="content-buttons-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
        {hasSpecification && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('specification', pilot.specification, `Specification - ${pilot.pilotName}`);
            }}
            style={{
              background: '#e0f2fe',
              color: '#0369a1',
              border: '1px solid #bae6fd',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📋 Spec
          </button>
        )}

        {hasDescription && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('description', pilot.description, `Description - ${pilot.pilotName}`);
            }}
            style={{
              background: '#f0f9ff',
              color: '#0c4a6e',
              border: '1px solid #e0f2fe',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📝 Desc
          </button>
        )}

        {hasCertificates && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('certificates', pilot.certifications, `Certificates - ${pilot.pilotName}`);
            }}
            style={{
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fde68a',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📜 Certs ({pilot.certifications.length})
          </button>
        )}

        {hasProfilePhoto && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('profilePhoto', pilot.profilePhoto, `Profile Photo - ${pilot.pilotName}`);
            }}
            style={{
              background: '#dbeafe',
              color: '#1e40af',
              border: '1px solid #bfdbfe',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📸 Photo
          </button>
        )}
      </div>
    );
  };

  // Helper to render bank validation status badge
  const renderBankValidationStatus = (pilot) => {
    const validation = pilot.bankValididation;
    if (!validation) return <span className="bank-status-badge status-pending">No Data</span>;

    if (validation.razorpayVerified) {
      return <span className="bank-status-badge status-approved">Verified</span>;
    } else if (validation.razorpayStatus === "pending") {
      return <span className="bank-status-badge status-pending">Pending</span>;
    } else if (validation.razorpayStatus === "failed") {
      return <span className="bank-status-badge status-rejected">Failed</span>;
    }
    return <span className="bank-status-badge status-pending">Not Verified</span>;
  };

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Buyer Pilots Management</h1>
            <p>Admin Dashboard • Manage Buyer Pilot Registrations • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={loading || pilots.length === 0}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Compact Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Pilots</div>
              <div className="compact-stat-value">{totalPilots}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Available Pilots</div>
              <div className="compact-stat-value">{availablePilots}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Buyers</div>
              <div className="compact-stat-value">{buyerGroups.length}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{statusStats.pending}</div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Buyer Pilot Registrations</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by buyer ID, name, email, pilot name, or company..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected", "deactivated"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    setProductsPage({});
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Buyers ({buyerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedBuyers.length} buyers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading buyer pilot data...</p>
                </div>
              ) : buyerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No buyers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("buyerId")}>
                        Buyer ID <SortIcon column="buyerId" />
                      </th>
                      <th onClick={() => handleSort("name")}>
                        Contact Info <SortIcon column="name" />
                      </th>
                      <th onClick={() => handleSort("totalPilots")}>
                        Pilots <SortIcon column="totalPilots" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBuyers.map((buyer) => {
                      const { pilots: buyerPilots, currentPage: pilotsCurrentPage, totalPages: pilotsTotalPages, totalPilots } = getPaginatedPilotsForBuyer(buyer.buyerId);

                      return (
                        <React.Fragment key={buyer.buyerId}>
                          <tr
                            className={`seller-row ${expandedBuyer === buyer.buyerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedBuyer(
                              expandedBuyer === buyer.buyerId ? null : buyer.buyerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedBuyer === buyer.buyerId ? '▼' : '▶'}
                                </span>
                                <strong>Buyer #{buyer.buyerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  👤 {buyer.buyerInfo?.name || "No name"}
                                </div>
                                <div className="seller-phone">
                                  📧 {buyer.buyerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {buyer.buyerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{buyer.totalPilots}</span>
                                <span className="count-label">pilots</span>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${buyer.approvedPilots} approved`}>
                                  {buyer.approvedPilots}
                                </span>
                                <span className="status-dot pending" title={`${buyer.pendingPilots} pending`}>
                                  {buyer.pendingPilots}
                                </span>
                                <span className="status-dot rejected" title={`${buyer.rejectedPilots} rejected`}>
                                  {buyer.rejectedPilots}
                                </span>
                                {buyer.deactivatedPilots > 0 && (
                                  <span className="status-dot rejected" title={`${buyer.deactivatedPilots} deactivated`}>
                                    {buyer.deactivatedPilots}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedBuyer(
                                    expandedBuyer === buyer.buyerId ? null : buyer.buyerId
                                  );
                                }}
                                title="View Pilots"
                              >
                                {expandedBuyer === buyer.buyerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>

                          {expandedBuyer === buyer.buyerId && (
                            <tr className="products-row">
                              <td colSpan="5">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Pilots from Buyer #{buyer.buyerId}</h4>
                                      <span className="products-count">
                                        {totalPilots} pilots • Showing {buyerPilots.length} on page {pilotsCurrentPage} of {pilotsTotalPages}
                                      </span>
                                    </div>
                                  </div>

                                  {totalPilots === 0 ? (
                                    <div className="no-products">
                                      <p>No pilots found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Pilot ID</th>
                                              <th>Pilot Details</th>
                                              <th>Company</th>
                                              <th>Location</th>
                                              <th>Price</th>
                                              <th>Availability</th>
                                              <th>Status</th>
                                              <th>Bank Details</th>
                                              <th>Content</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {buyerPilots.map((pilot) => (
                                              <tr key={pilot.buyerPilotId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {pilot.buyerPilotId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    {pilot.profilePhoto?.url && (
                                                      <img src={pilot.profilePhoto.url} alt={pilot.pilotName} className="drone-image" style={{ width: '48px', height: '48px', borderRadius: '8px' }} />
                                                    )}
                                                    <div>
                                                      <div className="drone-name">{pilot.pilotName}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{pilot.pilotCompany || "—"}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{pilot.pilotCompany || "—"}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{pilot.location || "N/A"}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {formatPrice(pilot.price?.perHour)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className={`quantity-cell ${!pilot.availability ? 'out-of-stock' : ''}`}>
                                                    {pilot.availability ? "Available" : "Busy"}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${pilot.adminStatus ? pilot.adminStatus.toLowerCase().replace('_', '-') : "pending"}`}>
                                                    {(pilot.adminStatus || "pending").toUpperCase()}
                                                  </span>
                                                </td>
                                                {/* Bank Details Column */}
                                                <td style={{ maxWidth: '180px' }}>
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ fontSize: '12px', fontWeight: '500' }}>
                                                      {pilot.bankDetails?.bankName ? pilot.bankDetails.bankName : "No Bank"}
                                                    </div>
                                                    {renderBankValidationStatus(pilot)}
                                                    <button
                                                      className="content-btn"
                                                      onClick={() => viewBankDetails(pilot)}
                                                      style={{
                                                        background: '#e6f7e6',
                                                        color: '#2e7d32',
                                                        border: '1px solid #c8e6c9',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        marginTop: '4px'
                                                      }}
                                                    >
                                                      View Details
                                                    </button>
                                                  </div>
                                                </td>
                                                <td style={{ maxWidth: '200px', minWidth: '150px' }}>
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                                      Click to view:
                                                    </div>
                                                    {renderContentButtons(pilot)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {(!pilot.adminStatus || pilot.adminStatus.toLowerCase() === "pending") && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "approved")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "rejected")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {pilot.adminStatus && pilot.adminStatus.toLowerCase() === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "pending")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "rejected")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {pilot.adminStatus && pilot.adminStatus.toLowerCase() === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "pending")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "approved")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {pilot.adminStatus && pilot.adminStatus.toLowerCase().includes("deactivated") && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateStatus(pilot.buyerPilotId, "approved")}
                                                          disabled={updatingIds.has(`${pilot.buyerPilotId}-approved`)}
                                                          title="Activate"
                                                        >
                                                          {updatingIds.has(`${pilot.buyerPilotId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewPilotDetails(pilot)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>

                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deletePilot(pilot.buyerPilotId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {pilotsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(buyer.buyerId, pilotsCurrentPage - 1)}
                                            disabled={pilotsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {pilotsCurrentPage} of {pilotsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(buyer.buyerId, pilotsCurrentPage + 1)}
                                            disabled={pilotsCurrentPage === pilotsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pilot Details Modal */}
      {viewingPilot && (
        <div className="modal-overlay" onClick={closePilotDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Pilot Details</h2>
              <button className="close-btn" onClick={closePilotDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-info-section">
                  {viewingPilot.profilePhoto?.url && (
                    <div className="drone-image-section">
                      <img
                        src={viewingPilot.profilePhoto.url}
                        alt={viewingPilot.pilotName}
                        className="drone-detail-image"
                        style={{ borderRadius: '12px', marginBottom: '20px' }}
                      />
                    </div>
                  )}

                  <div className="info-row">
                    <div className="info-label">Pilot ID</div>
                    <div className="info-value">{viewingPilot.buyerPilotId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingPilot.pilotName}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Email</div>
                    <div className="info-value">{viewingPilot.newemail}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Phone</div>
                    <div className="info-value">{viewingPilot.newphoneNumber}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Company</div>
                    <div className="info-value">{viewingPilot.pilotCompany || "—"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Location</div>
                    <div className="info-value">{viewingPilot.location || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Availability</div>
                    <div className="info-value">
                      <span className={`quantity-display ${!viewingPilot.availability ? 'out-of-stock' : ''}`}>
                        {viewingPilot.availability ? "Available" : "Busy"}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price (Per Hour)</div>
                    <div className="info-value price-value">{formatPrice(viewingPilot.price?.perHour)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price (Per Day)</div>
                    <div className="info-value price-value">{formatPrice(viewingPilot.price?.perDay)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingPilot.adminStatus ? viewingPilot.adminStatus.toLowerCase().replace('_', '-') : "pending"}`}>
                        {(viewingPilot.adminStatus || "pending").toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Buyer ID</div>
                    <div className="info-value">{viewingPilot.buyerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Buyer Name</div>
                    <div className="info-value">{viewingPilot.buyer?.name || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Buyer Email</div>
                    <div className="info-value">{viewingPilot.buyer?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Buyer Phone</div>
                    <div className="info-value">{viewingPilot.buyer?.phoneNumber || "N/A"}</div>
                  </div>

                  {/* Content buttons in modal */}
                  <div className="info-row full-width">
                    <div className="info-label">Content</div>
                    <div className="info-value" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {viewingPilot.specification && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('specification', viewingPilot.specification, `Specification - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📋 View Specification
                        </button>
                      )}

                      {viewingPilot.description && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('description', viewingPilot.description, `Description - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#f0f9ff',
                            color: '#0c4a6e',
                            border: '1px solid #e0f2fe',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📝 View Description
                        </button>
                      )}

                      {viewingPilot.certifications?.length > 0 && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('certificates', viewingPilot.certifications, `Certificates - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fde68a',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📜 View Certificates ({viewingPilot.certifications.length})
                        </button>
                      )}

                      {viewingPilot.profilePhoto?.url && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('profilePhoto', viewingPilot.profilePhoto, `Profile Photo - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#dbeafe',
                            color: '#1e40af',
                            border: '1px solid #bfdbfe',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📸 View Profile Photo
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Bank Details Section */}
                  <div className="info-section">
                    <div className="info-label">Bank Details</div>
                    {viewingPilot.bankDetails ? (
                      <div className="bank-details-grid">
                        <div className="info-row">
                          <div className="info-label">Account Holder</div>
                          <div className="info-value">{viewingPilot.bankDetails.accountHolderName || "—"}</div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Bank Name</div>
                          <div className="info-value">{viewingPilot.bankDetails.bankName || "—"}</div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Account Number</div>
                          <div className="info-value">
                            {viewingPilot.bankDetails.accountNumber
                              ? `****${viewingPilot.bankDetails.accountNumber.slice(-4)}`
                              : "—"}
                          </div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">IFSC Code</div>
                          <div className="info-value">{viewingPilot.bankDetails.ifscCode || "—"}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="info-value">No bank details provided</div>
                    )}
                  </div>

                  {/* Bank Validation Section */}
                  <div className="info-section">
                    <div className="info-label">Bank Validation</div>
                    {viewingPilot.bankValididation ? (
                      <div className="validation-details-grid">
                        <div className="info-row">
                          <div className="info-label">Razorpay Contact ID</div>
                          <div className="info-value">{viewingPilot.bankValididation.razorpayContactId || "—"}</div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Fund Account ID</div>
                          <div className="info-value">{viewingPilot.bankValididation.razorpayFundAccountId || "—"}</div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Validation Status</div>
                          <div className="info-value">
                            <span className={`status-badge status-${viewingPilot.bankValididation.razorpayStatus?.toLowerCase() || "pending"}`}>
                              {viewingPilot.bankValididation.razorpayStatus || "Pending"}
                            </span>
                          </div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Validation ID</div>
                          <div className="info-value">{viewingPilot.bankValididation.razorpayValidationId || "—"}</div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Verification Payment ID</div>
                          <div className="info-value">{viewingPilot.bankValididation.razorpayVerificationPaymentId || "—"}</div>
                        </div>
                        <div className="info-row">
                          <div className="info-label">Verified</div>
                          <div className="info-value">
                            {viewingPilot.bankValididation.razorpayVerified ? "✅ Yes" : "❌ No"}
                          </div>
                        </div>
                        {viewingPilot.bankValididation.razorpayVerifiedAt && (
                          <div className="info-row">
                            <div className="info-label">Verified At</div>
                            <div className="info-value">{new Date(viewingPilot.bankValididation.razorpayVerifiedAt).toLocaleString()}</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="info-value">No bank validation data</div>
                    )}
                  </div>
                </div>

                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {(!viewingPilot.adminStatus || viewingPilot.adminStatus.toLowerCase() === "pending") && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateStatus(viewingPilot.buyerPilotId, "approved");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPilot.buyerPilotId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateStatus(viewingPilot.buyerPilotId, "rejected");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPilot.buyerPilotId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingPilot.adminStatus && viewingPilot.adminStatus.toLowerCase() === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateStatus(viewingPilot.buyerPilotId, "pending");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPilot.buyerPilotId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateStatus(viewingPilot.buyerPilotId, "rejected");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPilot.buyerPilotId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingPilot.adminStatus && viewingPilot.adminStatus.toLowerCase() === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateStatus(viewingPilot.buyerPilotId, "pending");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPilot.buyerPilotId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateStatus(viewingPilot.buyerPilotId, "approved");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPilot.buyerPilotId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                      </>
                    )}

                    {viewingPilot.adminStatus && viewingPilot.adminStatus.toLowerCase().includes("deactivated") && (
                      <button
                        className="action-btn-large action-approve"
                        onClick={() => {
                          updateStatus(viewingPilot.buyerPilotId, "approved");
                          closePilotDetails();
                        }}
                        disabled={updatingIds.has(`${viewingPilot.buyerPilotId}-approved`)}
                        title="Activate"
                      >
                        {updatingIds.has(`${viewingPilot.buyerPilotId}-approved`) ? "..." : "✓ Activate"}
                      </button>
                    )}

                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this pilot?")) {
                          deletePilot(viewingPilot.buyerPilotId);
                          closePilotDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content View Modal */}
      {viewingContent.type && (
        <div className="modal-overlay" onClick={closeContent}>
          <div className="modal-content content-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh' }}>
            <div className="modal-header">
              <h2>{viewingContent.title}</h2>
              <button className="close-btn" onClick={closeContent}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {viewingContent.type === 'specification' && (
                <div className="specification-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Specification Details</h3>
                  <div className="content-text" style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    {viewingContent.content || 'No specification provided.'}
                  </div>
                </div>
              )}

              {viewingContent.type === 'description' && (
                <div className="description-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Description Details</h3>
                  <div className="content-text" style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    {viewingContent.content || 'No description provided.'}
                  </div>
                </div>
              )}

              {viewingContent.type === 'certificates' && (
                <div className="certificates-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Certificates</h3>
                  <div className="certificates-list" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {viewingContent.content.length === 0 ? (
                      <p style={{ color: '#6b7280', textAlign: 'center' }}>No certificates uploaded.</p>
                    ) : (
                      viewingContent.content.map((cert, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '16px',
                          backgroundColor: '#f0f9ff',
                          borderRadius: '8px',
                          border: '1px solid #bae6fd'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: '#0ea5e9',
                              color: 'white',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px'
                            }}>
                              📄
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: '#0369a1' }}>
                                Certificate {index + 1}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                Click to view/download
                              </div>
                            </div>
                          </div>
                          <a
                            href={cert.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontWeight: '500',
                              fontSize: '14px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                          >
                            View Certificate
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {viewingContent.type === 'profilePhoto' && (
                <div className="profile-photo-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Profile Photo</h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <img
                      src={viewingContent.content.url}
                      alt="Profile Photo"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '400px',
                        borderRadius: '12px',
                        border: '2px solid #d1d5db'
                      }}
                    />
                    <a
                      href={viewingContent.content.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '16px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      <span>📸</span>
                      <span>View Full Image</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bank Details Modal */}
      {viewingBankDetails && (
        <div className="modal-overlay" onClick={closeBankDetails}>
          <div className="modal-content bank-details-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Bank Details - {viewingBankDetails.pilotName}</h2>
              <button className="close-btn" onClick={closeBankDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="bank-details-full">
                <h3>Bank Account Information</h3>
                {viewingBankDetails.bankDetails ? (
                  <>
                    <div className="info-row">
                      <div className="info-label">Account Holder Name</div>
                      <div className="info-value">{viewingBankDetails.bankDetails.accountHolderName || "—"}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Bank Name</div>
                      <div className="info-value">{viewingBankDetails.bankDetails.bankName || "—"}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Account Number</div>
                      <div className="info-value">
                        {viewingBankDetails.bankDetails.accountNumber
                          ? viewingBankDetails.bankDetails.accountNumber
                          : "—"}
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">IFSC Code</div>
                      <div className="info-value">{viewingBankDetails.bankDetails.ifscCode || "—"}</div>
                    </div>
                  </>
                ) : (
                  <p>No bank details provided.</p>
                )}

                <h3 style={{ marginTop: '24px' }}>Bank Validation</h3>
                {viewingBankDetails.bankValididation ? (
                  <>
                    <div className="info-row">
                      <div className="info-label">Razorpay Contact ID</div>
                      <div className="info-value">{viewingBankDetails.bankValididation.razorpayContactId || "—"}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Fund Account ID</div>
                      <div className="info-value">{viewingBankDetails.bankValididation.razorpayFundAccountId || "—"}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Validation Status</div>
                      <div className="info-value">
                        <span className={`status-badge status-${viewingBankDetails.bankValididation.razorpayStatus?.toLowerCase() || "pending"}`}>
                          {viewingBankDetails.bankValididation.razorpayStatus || "Pending"}
                        </span>
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Validation ID</div>
                      <div className="info-value">{viewingBankDetails.bankValididation.razorpayValidationId || "—"}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Verification Payment ID</div>
                      <div className="info-value">{viewingBankDetails.bankValididation.razorpayVerificationPaymentId || "—"}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Verified</div>
                      <div className="info-value">
                        {viewingBankDetails.bankValididation.razorpayVerified ? "✅ Yes" : "❌ No"}
                      </div>
                    </div>
                    {viewingBankDetails.bankValididation.razorpayVerifiedAt && (
                      <div className="info-row">
                        <div className="info-label">Verified At</div>
                        <div className="info-value">{new Date(viewingBankDetails.bankValididation.razorpayVerifiedAt).toLocaleString()}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <p>No bank validation data available.</p>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closeBankDetails} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BuyerPilots;